import { useEffect, useState } from "react";
import { Landmark, Plus, X, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/msme-api";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";

const SOURCE_LABEL = { bank_cash: "Bank/Cash", manual: "Manual" };

/**
 * Payment Ledger Mapping — per-CLIENT (persists across FY sessions).
 *
 * A creditor settlement for §43B(h) isn't always a literal "Payment"
 * voucher against a bank/cash ledger — multi-division clients often
 * route the actual cash movement through the Head Office's books,
 * recorded as a debit to the creditor + credit to an intercompany
 * ledger. Flagging that ledger here means every future FY's payments
 * upload recognizes it automatically. Bank/Cash ledgers are
 * auto-detected on every Payments upload; anything else (e.g. "Due to
 * Head Office") is added manually here, searched from the client's
 * Ledger Mapping already loaded via the Data Library.
 */
export default function PaymentLedgerMapping({ clientId, period, divisionId, refreshKey, currentSessionId, onChanged }) {
  const [ledgers, setLedgers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState("");

  const load = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/payment-ledgers`);
      setLedgers(data.ledgers || []);
    } catch {
      // non-fatal — mapping is an enhancement, not a hard dependency
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [clientId, refreshKey]);

  const loadCatalog = async () => {
    if (catalog !== null || !clientId) return;
    setCatalogLoading(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/ledger-catalog`, {
        params: { period: period || "", division: divisionId || undefined },
      });
      setCatalog(data.ledgers || []);
    } catch {
      setCatalog([]);
    } finally {
      setCatalogLoading(false);
    }
  };

  const _reDeriveToast = (reDerived) => {
    if (!reDerived?.length) return;
    const mine = reDerived.find((r) => r.session_id === currentSessionId);
    const delta = reDerived.reduce((n, r) => n + (r.payment_count_after - r.payment_count_before), 0);
    if (mine && mine.payment_count_after !== mine.payment_count_before) {
      toast.info(`This session's payments re-checked: ${mine.payment_count_before} → ${mine.payment_count_after} settlements found`);
    } else if (delta !== 0) {
      toast.info(`Re-checked ${reDerived.length} session(s) for this client — ${delta > 0 ? "+" : ""}${delta} settlement(s) found`);
    }
    onChanged?.();
  };

  const addLedger = async (name) => {
    const clean = (name || "").trim();
    if (!clean) return;
    setAdding(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/payment-ledgers`, { name: clean });
      setLedgers(data.ledgers || []);
      toast.success(`"${clean}" flagged as a Payment Ledger`);
      setOpen(false);
      setQuery("");
      _reDeriveToast(data.re_derived_sessions);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add ledger");
    } finally {
      setAdding(false);
    }
  };

  const removeLedger = async (name) => {
    try {
      const { data } = await api.delete(`/clients/${clientId}/payment-ledgers/${encodeURIComponent(name)}`);
      setLedgers(data.ledgers || []);
      toast.success(`Removed "${name}"`);
      _reDeriveToast(data.re_derived_sessions);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove ledger");
    }
  };

  const filteredCatalog = (catalog || []).filter(
    (n) => !query || n.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="border border-gray-200 rounded-sm p-4" data-testid="payment-ledger-mapping">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <Landmark className="text-gray-700 mt-0.5" size={18} />
          <div>
            <div className="text-sm font-semibold text-gray-900">Payment Ledger Mapping</div>
            <p className="text-xs text-gray-600 mt-0.5 leading-relaxed max-w-xl">
              Ledgers that represent an actual settlement — Bank/Cash are auto-detected on
              upload; add others (e.g. "Due to Head Office" for multi-division routing) below.
              Saved per client, applies to every future year.
            </p>
          </div>
        </div>

        <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) loadCatalog(); }}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium border border-gray-300 rounded-sm px-3 py-1.5 hover:bg-gray-50 transition-colors"
              data-testid="add-payment-ledger-btn"
            >
              <Plus size={14} /> Add Ledger
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0" align="end">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Search Ledger Mapping…"
                value={query}
                onValueChange={setQuery}
                data-testid="payment-ledger-search-input"
              />
              <CommandList>
                {catalogLoading ? (
                  <div className="py-6 flex justify-center">
                    <Loader2 className="animate-spin text-gray-400" size={16} />
                  </div>
                ) : (
                  <>
                    <CommandEmpty className="py-4 text-xs text-gray-500 px-3">
                      {query.trim() ? (
                        <button
                          type="button"
                          className="text-gray-900 underline"
                          onClick={() => addLedger(query)}
                          data-testid="add-custom-ledger-btn"
                        >
                          Add "{query.trim()}" as a custom ledger
                        </button>
                      ) : (
                        "No ledgers found in this client's Ledger Mapping."
                      )}
                    </CommandEmpty>
                    <CommandGroup>
                      {filteredCatalog.slice(0, 50).map((name) => (
                        <CommandItem
                          key={name}
                          value={name}
                          onSelect={() => addLedger(name)}
                          disabled={adding}
                          data-testid={`ledger-catalog-item-${name}`}
                        >
                          <Search size={12} className="text-gray-400" />
                          <span className="truncate">{name}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <div className="mt-3 flex flex-wrap gap-2" data-testid="payment-ledger-chips">
        {loading ? (
          <div className="text-xs text-gray-500 flex items-center gap-2">
            <Loader2 className="animate-spin" size={12} /> Loading…
          </div>
        ) : ledgers.length === 0 ? (
          <div className="text-xs text-gray-500">
            No Payment Ledgers flagged yet — upload a Payments JSON to auto-detect Bank/Cash
            ledgers, or add one manually.
          </div>
        ) : (
          ledgers.map((l) => (
            <div
              key={l.name}
              className="inline-flex items-center gap-2 border border-gray-200 bg-gray-50 rounded-sm px-2.5 py-1 text-xs"
              data-testid={`payment-ledger-chip-${l.name}`}
            >
              <span className="font-medium text-gray-900">{l.name}</span>
              <span className="text-[10px] uppercase tracking-wide text-gray-500">
                {SOURCE_LABEL[l.source] || l.source}
              </span>
              <button
                type="button"
                onClick={() => removeLedger(l.name)}
                className="text-gray-400 hover:text-gray-900 transition-colors"
                data-testid={`remove-payment-ledger-${l.name}`}
              >
                <X size={12} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
